/**
 * Plan Polling Hook
 * Polls the plan endpoint to detect when async operations complete
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import * as api from './api';
import type { BusinessPlanWithSections, SectionKey } from './types';

interface UsePlanPollingOptions {
  /** Poll interval in milliseconds (default: 2000) */
  pollInterval?: number;
  /** Whether polling is enabled (default: false) */
  enabled?: boolean;
  /** Callback when plan updates */
  onUpdate?: (plan: BusinessPlanWithSections) => void;
}

/**
 * Hook to poll for plan updates during async operations
 */
export function usePlanPolling(
  planId: string | null,
  options: UsePlanPollingOptions = {}
) {
  const { pollInterval = 2000, enabled = false, onUpdate } = options;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const poll = useCallback(async () => {
    if (!planId) return null;

    try {
      const plan = await api.getPlan(planId);
      onUpdate?.(plan);
      return plan;
    } catch (error) {
      console.error('[Foundry] Polling error:', error);
      return null;
    }
  }, [planId, onUpdate]);

  // Start/stop polling based on enabled flag
   
  useEffect(() => {
    if (!enabled || !planId) {
      stopPolling();
      return;
    }

    setIsPolling(true);

    // Initial poll
    poll();

    // Set up interval
    intervalRef.current = setInterval(poll, pollInterval);

    return () => {
      stopPolling();
    };
  }, [enabled, planId, pollInterval, poll, stopPolling]);
   

  return {
    isPolling,
    stopPolling,
    pollNow: poll,
  };
}

/**
 * Check if any section is currently processing
 */
export function hasProcessingSections(plan: BusinessPlanWithSections | null): boolean {
  if (!plan?.sections) return false;
  return plan.sections.some(s => s.status === 'processing');
}

/**
 * Check if canvas is currently generating
 */
export function isCanvasGenerating(plan: BusinessPlanWithSections | null): boolean {
  if (!plan?.user_context) return false;
  // Use type assertion through unknown for internal flags
  const ctx = plan.user_context as unknown as Record<string, unknown>;
  return ctx._canvas_generating === true;
}

/**
 * Get canvas generation error if any
 */
export function getCanvasError(plan: BusinessPlanWithSections | null): string | null {
  if (!plan?.user_context) return null;
  // Use type assertion through unknown for internal flags
  const ctx = plan.user_context as unknown as Record<string, unknown>;
  const error = ctx._canvas_error;
  return typeof error === 'string' ? error : null;
}

/**
 * Get sections that are currently processing
 */
export function getProcessingSections(plan: BusinessPlanWithSections | null): SectionKey[] {
  if (!plan?.sections) return [];
  return plan.sections
    .filter(s => s.status === 'processing')
    .map(s => s.section_key);
}

/**
 * Get sections that failed
 */
export function getFailedSections(plan: BusinessPlanWithSections | null): SectionKey[] {
  if (!plan?.sections) return [];
  return plan.sections
    .filter(s => s.status === 'failed')
    .map(s => s.section_key);
}
